// Scoring for MoSAIC PSYOPS behavioral screenings.
//
// The score formula is unchanged from the pre-refactor assessments: a
// weighted mean of the selected options' points (1-5) normalised against the
// 5-point maximum, expressed 0-100.
//
// The rating bands did not exist in code before — the results table was static
// JSX, and 50% fell between the printed "49% - 30%" and "51% - 74%" ranges.
// Bands below cover every integer 0-100 with no gap.

export const RATING_BANDS = [
  { id: 'severe', label: 'Severe', min: 75, max: 100, rangeLabel: '> 75%', color: 'bg-red-600' },
  { id: 'high', label: 'High', min: 51, max: 74, rangeLabel: '51% - 74%', color: 'bg-red-500' },
  { id: 'medium', label: 'Medium', min: 30, max: 50, rangeLabel: '50% - 30%', color: 'bg-orange-400' },
  { id: 'low', label: 'Low', min: 0, max: 29, rangeLabel: '29% - 0%', color: 'bg-blue-500' },
];

export const isMultiSelect = (question) => question.question.includes('(Select all that apply)');

// Weighted mean of selected points, normalised to 0-100.
export function calculateScore(questions, answers) {
  let totalPoints = 0;
  let totalWeight = 0;

  questions.forEach((question, index) => {
    const answer = answers[index];
    if (answer === undefined || answer === null) return;

    const selected = Array.isArray(answer) ? answer : [answer];
    selected.forEach((optionIndex) => {
      const option = question.options[optionIndex];
      if (!option) return;
      totalPoints += option.points * question.weight;
      totalWeight += question.weight;
    });
  });

  return totalWeight > 0 ? Math.round((totalPoints / (totalWeight * 5)) * 100) : 0;
}

export function getRating(score) {
  return RATING_BANDS.find((band) => score >= band.min && score <= band.max) || RATING_BANDS[RATING_BANDS.length - 1];
}

// Points (1-5) on a single answer map onto the same four bands, so the
// distribution reads in the same language as the rating. Multi-select answers
// count once, using the mean points of the options selected.
function bandForPoints(points) {
  if (points >= 5) return 'severe';
  if (points >= 4) return 'high';
  if (points >= 3) return 'medium';
  return 'low';
}

// How many of the user's answers landed in each band. Replaces the hardcoded
// 6 / 7 / 8 / 4 placeholder the old results screen displayed.
export function responseDistribution(questions, answers) {
  const counts = { severe: 0, high: 0, medium: 0, low: 0 };

  questions.forEach((question, index) => {
    const answer = answers[index];
    if (answer === undefined || answer === null) return;

    const selected = (Array.isArray(answer) ? answer : [answer])
      .map((optionIndex) => question.options[optionIndex])
      .filter(Boolean);
    if (!selected.length) return;

    const meanPoints = selected.reduce((sum, o) => sum + o.points, 0) / selected.length;
    counts[bandForPoints(meanPoints)] += 1;
  });

  return counts;
}
